import { useClubPublicConfig } from './clubPublicConfig';

/**
 * Modules du club, choisis par la Ligue dans MySquash : clés publiques "modules.xxx" = "true"/"false".
 * Un module absent ou non reçu (API injoignable, config pas encore chargée) est considéré comme actif :
 * par défaut l'ERP est identique à aujourd'hui.
 *
 * Modules : box_league, court_booking, open_games (recherche de partenaire :
 * parties publiques, avec la réservation de terrains), smartpadel (avec la réservation de terrains), community (communauté : fil, posts), smartpadel, shop, tournaments (mêmes noms que
 * `app_config.modules` côté mobile ; un module enfant n'est actif qu'avec son parent, voir MODULE_PARENTS). open_games et community n'ont pas de page ERP dédiée (fonctions joueur, l'ordre du fil
 * communauté n'est plus réglable dans l'ERP). La file d'attente suit box_league.
 *
 * Modules ajoutés ensuite (même règle) : live_scoring (score en direct, arbitrage compris : pages publiques
 * /terrain/:n, /screen, OBS, action « Arbitrer », mot de passe et désignation d'arbitre — l'ancien module live_referee y
 * est fusionné depuis le 2026-10-09 ; l'API l'expose encore en miroir pour les apps installées, l'ERP ne le lit plus),
 * interclub, memberships (abonnements), wallet (crédit terrain), online_payment (paiement en ligne : l'API traite alors le
 * prestataire actif comme « sur place »), gamification (badges) et advanced_stats (pas de page ERP dédiée). L'API ne
 * bloque jamais le staff : ce sont les fonctions joueur qui sont coupées ; l'ERP masque seulement ce qui n'aurait plus
 * d'effet.
 *
 * Anciens modules qui ne sont plus des interrupteurs (2026-10-09, voir DERIVED_MODULES) : match_of_week suit box_league,
 * broadcast (écrans & diffusion : /screen et OBS) suit live_scoring, ratings (classement Elo : réglages « Matchs
 * classés ») et messaging (canal « Message privé » des notifications) sont toujours actifs. isModuleOn les résout ici,
 * quelle que soit la valeur encore renvoyée par l'API (ancienne ligne stockée).
 *
 * Tournois (2026-10-09) : TOUJOURS visibles (menu, page, onglets Matchs et tableau de bord) — `tournaments` est résolu à
 * true. Le module de la Ligue s'appelle maintenant « Tournois amicaux » et ne règle que la création d'un tournoi par le
 * club (bouton « Nouveau tournoi ») : `isModuleOn(config, 'tournaments_friendly')` lit `modules.tournaments_friendly`
 * (API à jour, qui renvoie toujours `modules.tournaments` = "true"), sinon `modules.tournaments` (API plus ancienne).
 */
/**
 * Ancien module → module dont il prend la valeur (true = toujours actif). Même règle que
 * ClubConfigRegistry.RetiredModuleKeys côté API.
 */
export const DERIVED_MODULES = {
  live_referee: 'live_scoring',
  match_of_week: 'box_league',
  broadcast: 'live_scoring',
  ratings: true,
  messaging: true,
  // Tournois toujours visibles (2026-10-09) ; la création par le club suit FRIENDLY_TOURNAMENTS.
  tournaments: true,
};

/** Module « Tournois amicaux » : le club peut créer ses propres tournois (POST /Tournament/create). */
export const FRIENDLY_TOURNAMENTS = 'tournaments_friendly';

/** Module enfant → module parent (même règle que ModuleGate côté API) : l'enfant est coupé avec son parent. */
export const MODULE_PARENTS = {
  open_games: 'court_booking',
  smartpadel: 'court_booking',
};

export function isModuleOn(config, module) {
  if (module === FRIENDLY_TOURNAMENTS) {
    // API à jour : vraie valeur sous tournaments_friendly ; API plus ancienne : modules.tournaments.
    const value = config?.['modules.tournaments_friendly'] ?? config?.['modules.tournaments'];
    return value !== 'false';
  }
  const derived = DERIVED_MODULES[module];
  if (derived === true) return true;
  if (derived) return isModuleOn(config, derived);
  if (config?.[`modules.${module}`] === 'false') return false;
  const parent = MODULE_PARENTS[module];
  return !parent || isModuleOn(config, parent);
}

/**
 * Module requis par une page `/app/...` (préfixe le plus long gagne). null = toujours visible.
 * `modules` : tous requis ; `anyModules` : au moins un (page partagée entre plusieurs modules).
 */
const ROUTE_MODULES = [
  // Écran Matchs : toujours visible (onglets Club et Tournoi) ; l'onglet Box suit son module (ErpMatchesHubPage).
  // Tournois (/app/tournaments) : toujours visibles depuis le 2026-10-09 (seule la création suit « Tournois amicaux »).
  { to: '/app/catalog', modules: ['box_league'] },
  { to: '/app/season-mirror', modules: ['box_league'] },
  { to: '/app/classement-print', modules: ['box_league'] },
  { to: '/app/waiting-list', modules: ['box_league'] },
  { to: '/app/motw-predictions', modules: ['box_league'] },
  { to: '/app/terrains/eclairage', modules: ['court_booking', 'smartpadel'] },
  // Boutique : articles et commandes (shop) ; les abonnements (memberships) y sont des articles vendus par commande,
  // donc articles et commandes restent ouverts avec l'un des deux. Ancienne fiche formule : abonnements.
  { to: '/app/shop', anyModules: ['shop', 'memberships'] },
  { to: '/app/shop/formules', modules: ['memberships'] },
  { to: '/app/shop/articles', anyModules: ['shop', 'memberships'] },
  { to: '/app/shop/commandes', anyModules: ['shop', 'memberships'] },
  { to: '/app/interclubs', modules: ['interclub'] },
  // Ancienne page « Adhésions & crédits » (redirige vers Joueurs → Abonnés) : memberships ou wallet.
  { to: '/app/memberships', anyModules: ['memberships', 'wallet'] },
  { to: '/app/badges', modules: ['gamification'] },
  // Replays des streams YouTube des terrains : le bandeau lu est celui du score en direct.
  { to: '/app/replays', modules: ['live_scoring'] },
];

/** true si la page (chemin `/app/...`) est accessible avec la configuration de modules courante. */
export function isRouteEnabled(config, path) {
  const rule = ROUTE_MODULES.filter((r) => path === r.to || path.startsWith(`${r.to}/`)).sort(
    (a, b) => b.to.length - a.to.length
  )[0];
  if (!rule) return true;
  if (rule.anyModules && !rule.anyModules.some((m) => isModuleOn(config, m))) return false;
  return (rule.modules || []).every((m) => isModuleOn(config, m));
}

export function useModuleEnabled(module) {
  return isModuleOn(useClubPublicConfig(), module);
}

/** Rend `children` seulement si le module est actif (champ ou action d'un module dans une page partagée). */
export function ModuleOnly({ module, children }) {
  return useModuleEnabled(module) ? children : null;
}
